import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'

import { Button } from '@/components/ui/Button'
import { AuthLayout } from '@/features/auth/AuthLayout'
import { RegisterForm } from '@/features/auth/RegisterForm'
import { invitePreviewQueryOptions, meQueryOptions } from '@/features/auth/api'
import { useJoinLounge } from '@/features/lounges/api'
import { errorMessage } from '@/lib/graphql'

/**
 * An invite link.
 *
 * Public on purpose — the whole point is that someone with no account can
 * follow it. Both queries answer for a signed-out visitor, so the page decides
 * between "register and join" and "join" without a redirect in between.
 */
export const Route = createFileRoute('/invite/$code')({
  loader: async ({ context: { queryClient }, params: { code } }) => {
    const [preview, { me }] = await Promise.all([
      queryClient.ensureQueryData(invitePreviewQueryOptions(code)),
      queryClient.ensureQueryData(meQueryOptions()),
    ])

    return { preview: preview.invitePreview, me }
  },
  component: InvitePage,
})

function InvitePage() {
  const { code } = Route.useParams()
  const { preview, me } = Route.useLoaderData()
  const navigate = useNavigate()
  const join = useJoinLounge()

  // An unknown code and an expired one are told apart deliberately: "this link
  // has expired" is actionable, "wrong link" is not, and an invite code carries
  // far too much entropy for the distinction to be an enumeration oracle.
  if (!preview) {
    return (
      <AuthLayout
        title="That link does not work"
        subtitle="It may have been mistyped, or the lounge it pointed at is gone."
      >
        <Link to="/login" className="btn btn-secondary btn-block">
          Go to nook
        </Link>
      </AuthLayout>
    )
  }

  if (!preview.isValid) {
    return (
      <AuthLayout
        title="This invite has expired"
        subtitle={`The link to ${preview.loungeName} is no longer good. Ask whoever sent it for a fresh one.`}
      >
        <Link to="/login" className="btn btn-secondary btn-block">
          Go to nook
        </Link>
      </AuthLayout>
    )
  }

  const landInLounge = () => {
    void navigate({ to: '/l/$loungeSlug', params: { loungeSlug: preview.loungeSlug } })
  }

  // Already signed in: one button, no forms.
  if (me) {
    return (
      <AuthLayout
        title={`Join ${preview.loungeName}?`}
        subtitle={`You are signed in as ${me.displayName}.`}
      >
        {join.isError && (
          <p className="form-error" role="alert">
            {errorMessage(join.error, 'You could not be added to that lounge.')}
          </p>
        )}

        <Button
          variant="primary"
          block
          disabled={join.isPending}
          onClick={() => join.mutate(code, { onSuccess: landInLounge })}
        >
          {join.isPending ? 'Joining…' : `Join ${preview.loungeName}`}
        </Button>
      </AuthLayout>
    )
  }

  // No account yet. Registering with the code attached does both in one call,
  // which is the only thing that works when the instance is invite-only.
  return (
    <AuthLayout
      title={`You are invited to ${preview.loungeName}`}
      subtitle="Make an account and you will land straight inside."
    >
      <RegisterForm inviteCode={code} onRegistered={landInLounge} />

      <p className="muted auth-aside">
        Already have an account?{' '}
        <Link to="/login" search={{ redirect: `/invite/${code}` }}>
          Sign in
        </Link>
      </p>
    </AuthLayout>
  )
}
